// 현재 내역서 — 원 계약 견적 줄 + 승인된 변경공사 줄을 줄 단위로 합친다(저장소를 읽지 않는 한 벌 · 합계는 currentTotalOf 로 낸다).

import { formatQty } from "../../lib/format";
import { contractVatModeOf, currentTotalOf } from "./calc";
import { quoteItemTreeOf, sourceLabelOf, type SourceItem, type SourceTrade } from "./source";
import type { ChangeOrder, ChangeOrderItem } from "./types";
import { CHANGE_ORDER_FREE_LINE_LABEL, CHANGE_ORDER_LEGACY_DEDUCT_LABEL } from "./view";
import type { Contract } from "../contract/rows";
import { contractSharePath } from "../contract/view";
import { DELETED_SPACE_LABEL, UNLISTED_SPACE_LABEL } from "../portfolio/pairs";
import { checked } from "../money/checked";
import { storedSplitOf, type VatMode } from "../money/vat";
import { SUPPLY_LABEL, VAT_LABEL, vatBasisLabel } from "../quote/snapshot-vat";
import { UNASSIGNED_SPACE_LABEL } from "../quote/space-totals";
import type { Quote } from "../quote/types";

export const STATEMENT_GROUPINGS = ["trade", "space"] as const;
export type StatementGrouping = (typeof STATEMENT_GROUPINGS)[number];

export const STATEMENT_GROUPING_LABEL: Record<StatementGrouping, string> = { trade: "공종별", space: "공간별" };

export const STATEMENT_LEGACY_DEDUCT_LABEL = CHANGE_ORDER_LEGACY_DEDUCT_LABEL;

export const STATEMENT_ORPHAN_LABEL = "분류 없음";
export const CUSTOMER_STATEMENT_ORPHAN_LABEL = "기타";

export const statementGroupingOf = (raw: string | string[] | undefined): StatementGrouping =>
  raw === "space" ? "space" : "trade";

export interface StatementQuoteInput {
  quote: Pick<Quote, "kind" | "mgmt_fee" | "gross" | "total" | "supply_amount" | "vat_amount">;
  trades: readonly SourceTrade[];
  items: readonly SourceItem[];
}

export interface StatementChangeOrderInput {
  change_order: Pick<ChangeOrder, "id" | "seq" | "title" | "status" | "delta_total" | "delta_supply" | "delta_vat">;
  items: readonly Pick<
    ChangeOrderItem,
    | "id"
    | "seq"
    | "direction"
    | "item"
    | "unit"
    | "qty"
    | "amount"
    | "note"
    | "source_quote_item_id"
    | "trade_name"
    | "space_code"
    | "space_label"
    | "source_label"
  >[];
}

// 고객 링크가 토큰으로 읽어 오는 원 견적 판·공간 설정 — 계산은 buildCurrentStatement 한 벌이 한다.
export interface StatementShareSource {
  quote: StatementQuoteInput | null;
  spaceSetting: string | null;
}

export interface CurrentStatementInput {
  contract: Pick<Contract, "total_amount" | "supply_amount" | "vat_amount"> & Partial<Pick<Contract, "vat_included">>;
  // 계약이 물린 견적 판(contract.quote_id) — 못 읽었으면 null(원 줄 없이 계약금액 조정 한 줄로 남는다).
  quote: StatementQuoteInput | null;
  // 계약의 변경공사 전부 — 승인(ACCEPTED)만 남기고 나머지는 여기서 버린다.
  changeOrders: readonly StatementChangeOrderInput[];
  // 회사 공간 목록(순서가 곧 공간별 묶음 순서).
  spaces: readonly { code: string; label: string }[];
  grouping: StatementGrouping;
  // 고객이면 내부 표기(분류 없음·지운 공간)를 중립 문구로 바꾼다 — 금액은 같다.
  audience?: StatementAudience;
}

export interface StatementColumn {
  id: string;
  seq: number;
  title: string;
  delta_total: number;
  delta_supply: number;
  delta_vat: number;
  // 문서 증감액(별도 계약은 공급가 증감) − 항목 합(보통 0) — 0 이 아니면 「변경 조정」 줄로 드러낸다.
  adjust: number;
}

// 열 하나의 증감 — 이 줄을 건드리지 않은 차수는 null.
export interface StatementDelta {
  qty: number;
  amount: number;
}

export interface StatementLine {
  key: string;
  label: string;
  unit: string;
  trade_name: string | null;
  space_label: string | null;
  // 연결 없는 줄의 원 견적 표기(원 견적 줄이 지워진 옛 연결) · 메모.
  origin: string | null;
  original_qty: number;
  original_amount: number;
  deltas: (StatementDelta | null)[];
  // 단위 혼합이거나 원 줄 없는 줄의 수량이 음수(감액)면 null.
  current_qty: number | null;
  // 원 견적과 단위가 다른 증감이 섞여 수량을 더할 수 없다.
  unit_mixed: boolean;
  current_amount: number;
}

// orphan — 공종을 못 찾은 견적 줄(「분류 없음」).
export type StatementGroupKind = "quote" | "orphan" | "free" | "legacy";

export interface StatementGroup {
  key: string;
  kind: StatementGroupKind;
  label: string;
  lines: StatementLine[];
  original: number;
  deltas: number[];
  current: number;
}

export interface StatementTotals {
  // 계약의 부가세 기준 — 별도(EXCL)면 줄·관리비·절사·조정이 전부 공급가이고 그 아래 부가세 줄이 붙는다.
  vat_mode: VatMode;
  // 줄 합(관리비·절사 전).
  lines: { original: number; deltas: number[]; current: number };
  mgmt_fee: number;
  // 절사 — 0 이하.
  rounding: number;
  // 원 계약금액(별도면 공급가) − (줄 합 + 관리비 + 절사) — 계약서에서 금액을 고쳤거나 줄이 없는 견적(가견적)이면 0 이 아니다.
  contract_adjust: number;
  change_adjust: number[];
  total: { original: number; deltas: number[]; current: number };
  original_supply: number;
  original_vat: number;
  current_supply: number;
  current_vat: number;
}

export type ContractAdjustReason = "NO_QUOTE" | "ROUGH" | "EDITED" | null;

export interface CurrentStatement {
  grouping: StatementGrouping;
  columns: StatementColumn[];
  groups: StatementGroup[];
  totals: StatementTotals;
  contract_adjust_reason: ContractAdjustReason;
}

const sum = (values: readonly number[], what: string): number =>
  checked(
    values.reduce((a, b) => a + b, 0),
    what
  );

// 수량은 소수 셋째 자리(numeric(14,3))까지라 정수로 올려 더한다(source.ts 와 같은 자).
const milli = (qty: number): number => Math.round(qty * 1000);

interface LeafRow {
  item: SourceItem;
  // 공종을 못 찾은 줄(orphan)은 null — 「분류 없음」 묶음으로 드러낸다(부모만 깨진 줄은 제 공종에 남는다).
  trade: SourceTrade | null;
  trade_name: string;
  order: number;
  space_code: string | null;
}

// 공종 순 → 항목 순(SET 부모 자리에 자식을 끼운다) · 부모는 빼고 부모 공간은 자식이 물려받는다(space-totals 와 같은 규칙).
const leafRows = (quote: StatementQuoteInput): LeafRow[] => {
  const trades = [...quote.trades].sort((a, b) => a.sort_order - b.sort_order);
  const byId = new Map(quote.items.map((i) => [i.id, i]));
  // 순환(A↔B) 멤버는 각자 잎으로 드러낸다 — 고르기 목록과 같은 판정(source.ts).
  const { cyclic, hasChildren } = quoteItemTreeOf(quote.items);
  // 가장 가까운 조상의 공간 — 순환 멤버에서 멈춘다(walk 의 `item.space_code || inherited` 와 같은 값).
  const spaceOf = (item: SourceItem): string | null => {
    for (let cur: SourceItem | undefined = item; cur; cur = cyclic.has(cur.id) || !cur.parent_id ? undefined : byId.get(cur.parent_id)) {
      if (cur.space_code) return cur.space_code;
    }
    return null;
  };
  const rows: LeafRow[] = [];
  const visited = new Set<string>();
  const bySort = (a: SourceItem, b: SourceItem) => a.sort_order - b.sort_order;
  for (const trade of trades) {
    const walk = (parentId: string | null, inherited: string | null): void => {
      for (const item of quote.items.filter((i) => i.quote_trade_id === trade.id && i.parent_id === parentId).sort(bySort)) {
        if (visited.has(item.id)) continue;
        visited.add(item.id);
        const space = item.space_code || inherited;
        if (hasChildren(item.id)) walk(item.id, space);
        else rows.push({ item, trade, trade_name: trade.name, order: rows.length, space_code: space });
      }
    };
    walk(null, null);
  }
  // 걷지 못한 잎(공종이 없거나 부모가 없거나 순환인 줄)도 금액을 숨기지 않고 줄로 남긴다 — 자식 있는 부모는 자식이 센다.
  const tradeById = new Map(quote.trades.map((t) => [t.id, t]));
  for (const item of [...quote.items].sort(bySort)) {
    if (visited.has(item.id) || hasChildren(item.id)) continue;
    const trade = tradeById.get(item.quote_trade_id) ?? null;
    rows.push({ item, trade, trade_name: trade?.name ?? "", order: rows.length, space_code: spaceOf(item) });
  }
  return rows;
};

interface Draft {
  line: StatementLine;
  milliQty: number;
  qtyOk: boolean;
  // 원 견적 줄이 있는 줄인가 — 없으면(추가 항목·기타 감액) 음수 현재 수량은 뜻이 없어 비운다.
  hasOriginal: boolean;
}

type DraftBase = Omit<StatementLine, "key" | "deltas" | "current_qty" | "unit_mixed" | "current_amount">;

const newDraft = (key: string, base: DraftBase, width: number, hasOriginal: boolean): Draft => ({
  line: {
    key,
    ...base,
    deltas: Array.from({ length: width }, () => null),
    current_qty: null,
    unit_mixed: false,
    current_amount: base.original_amount,
  },
  milliQty: milli(base.original_qty),
  qtyOk: true,
  hasOriginal,
});

// 단위 비교 — 대소문자·공백·전각(NFKC: ㎡ = m2)을 무시한다(표시는 원 견적 단위 그대로).
const unitKey = (unit: string): string => unit.normalize("NFKC").replace(/\s+/g, "").toLowerCase();

const addDelta = (draft: Draft, col: number, qty: number, amount: number): void => {
  const prev = draft.line.deltas[col];
  draft.line.deltas[col] = prev
    ? { qty: (milli(prev.qty) + milli(qty)) / 1000, amount: checked(prev.amount + amount, "증감액") }
    : { qty, amount };
  draft.milliQty += milli(qty);
  draft.line.current_amount = checked(draft.line.current_amount + amount, "현재 금액");
};

const finish = (draft: Draft): StatementLine => ({
  ...draft.line,
  current_qty: !draft.qtyOk || (!draft.hasOriginal && draft.milliQty < 0) ? null : draft.milliQty / 1000,
  unit_mixed: !draft.qtyOk,
});

// 줄 아래 작은 글 — 견적 묶음은 묶음 기준이 아닌 쪽(공종별이면 공간, 공간별이면 공종)만, 그 밖(분류 없음·추가·기타 감액)은 둘 다 · 화면·인쇄 공용.
export const statementTagOf = (line: StatementLine, grouping: StatementGrouping, kind: StatementGroupKind): string => {
  const all = kind !== "quote";
  return [all || grouping === "space" ? line.trade_name : null, all || grouping === "trade" ? line.space_label : null, line.origin]
    .filter((v): v is string => Boolean(v))
    .join(" · ");
};

// 현재 수량 표기 — 화면·인쇄가 같이 쓴다(비울 줄이면 null · 고객은 「단위 혼합」 대신 비운다).
export const statementQtyText = (
  line: Pick<StatementLine, "current_qty" | "unit_mixed">,
  audience: StatementAudience = "staff"
): string | null =>
  line.unit_mixed ? (audience === "customer" ? null : "단위 혼합") : line.current_qty === null ? null : formatQty(line.current_qty);

const groupOf = (key: string, kind: StatementGroupKind, label: string, lines: StatementLine[], width: number): StatementGroup => ({
  key,
  kind,
  label,
  lines,
  original: sum(
    lines.map((l) => l.original_amount),
    "원 금액"
  ),
  deltas: Array.from({ length: width }, (_, col) =>
    sum(
      lines.map((l) => l.deltas[col]?.amount ?? 0),
      "증감액"
    )
  ),
  current: sum(
    lines.map((l) => l.current_amount),
    "현재 금액"
  ),
});

// 설정 화면이 저장하는 코드 모양(codes-actions.ts 의 코드 규칙) — 이 모양의 사본은 라벨이 아니라 옛 사본이 남긴 코드 원문이다.
const INTERNAL_CODE = /^[A-Za-z0-9_-]+$/;

export const buildCurrentStatement = (input: CurrentStatementInput): CurrentStatement => {
  const accepted = input.changeOrders
    .filter((co) => co.change_order.status === "ACCEPTED")
    .sort((a, b) => a.change_order.seq - b.change_order.seq);
  const width = accepted.length;
  const excl = contractVatModeOf(input.contract) === "EXCL";
  const customer = input.audience === "customer";
  const spaceLabel = new Map(input.spaces.map((s) => [s.code, s.label]));
  // 목록 밖(설정에서 지운) 코드는 코드 원문 대신 — 고객은 「기타 공간」, 직원은 「삭제된 공간」(사례 화면과 같은 표기).
  const unlistedLabel = customer ? UNLISTED_SPACE_LABEL : DELETED_SPACE_LABEL;
  const labelOfSpace = (code: string | null): string | null => (code ? spaceLabel.get(code) ?? unlistedLabel : null);
  // 변경 줄 공간 사본 — 코드와 다른 사본은 그대로, 코드와 같으면 지금 설정 → (코드 모양이 아닌 옛 한글 코드만) 사본 → 목록 밖 표기 순.
  const labelOfRow = (code: string | null | undefined, snapshot: string | null | undefined): string | null => {
    if (snapshot && snapshot !== code) return snapshot;
    const current = code ? spaceLabel.get(code) : undefined;
    if (current) return current;
    return snapshot && !INTERNAL_CODE.test(snapshot) ? snapshot : labelOfSpace(code ?? null);
  };

  const leaves = input.quote ? leafRows(input.quote) : [];
  const drafts = new Map<string, { draft: Draft; leaf: LeafRow }>();
  for (const leaf of leaves) {
    drafts.set(leaf.item.id, {
      leaf,
      draft: newDraft(
        leaf.item.id,
        {
          label: sourceLabelOf(leaf.item, leaf.trade_name),
          unit: leaf.item.unit,
          trade_name: leaf.trade_name || null,
          space_label: labelOfSpace(leaf.space_code),
          origin: null,
          original_qty: leaf.item.qty,
          original_amount: leaf.item.amount,
        },
        width,
        true
      ),
    });
  }

  const free: Draft[] = [];
  const legacy: Draft[] = [];
  const columns: StatementColumn[] = accepted.map((co, col) => {
    const items = [...co.items].sort((a, b) => a.seq - b.seq);
    for (const it of items) {
      const sign = it.direction === "DEDUCT" ? -1 : 1;
      const qty = sign * it.qty;
      const amount = sign * it.amount;
      const target = it.source_quote_item_id ? drafts.get(it.source_quote_item_id) : undefined;
      if (target) {
        addDelta(target.draft, col, qty, amount);
        if (unitKey(it.unit) !== unitKey(target.leaf.item.unit)) target.draft.qtyOk = false;
        continue;
      }
      // 연결 없음(또는 원 견적 줄이 사라진 옛 연결) — 추가는 「추가 항목」, 감액은 「기타 감액」 에 차수 줄 그대로 둔다.
      const draft = newDraft(
        `${co.change_order.id}:${it.id}`,
        {
          label: it.item,
          unit: it.unit,
          trade_name: it.trade_name ?? null,
          space_label: labelOfRow(it.space_code, it.space_label),
          origin: it.source_label ? `원 견적: ${it.source_label}` : it.note || null,
          original_qty: 0,
          original_amount: 0,
        },
        width,
        false
      );
      addDelta(draft, col, qty, amount);
      (it.direction === "ADD" ? free : legacy).push(draft);
    }
    const itemsSum = sum(
      items.map((it) => (it.direction === "DEDUCT" ? -it.amount : it.amount)),
      "증감액"
    );
    return {
      id: co.change_order.id,
      seq: co.change_order.seq,
      title: co.change_order.title,
      delta_total: co.change_order.delta_total,
      delta_supply: co.change_order.delta_supply,
      delta_vat: co.change_order.delta_vat,
      adjust: checked((excl ? co.change_order.delta_supply : co.change_order.delta_total) - itemsSum, "변경 조정"),
    };
  });

  const quoteLines = leaves.map((leaf) => ({ leaf, line: finish(drafts.get(leaf.item.id)!.draft) }));
  const groups: StatementGroup[] = [];
  if (input.grouping === "trade") {
    const byTrade = new Map<string, { trade: SourceTrade; lines: StatementLine[] }>();
    const orphans: StatementLine[] = [];
    for (const { leaf, line } of quoteLines) {
      if (!leaf.trade) {
        orphans.push(line);
        continue;
      }
      const bucket = byTrade.get(leaf.trade.id) ?? { trade: leaf.trade, lines: [] };
      bucket.lines.push(line);
      byTrade.set(leaf.trade.id, bucket);
    }
    // 부모만 깨진 줄은 걷은 줄 뒤에 붙으므로 묶음 순서는 공종 순으로 다시 맞춘다.
    for (const { trade, lines } of [...byTrade.values()].sort((a, b) => a.trade.sort_order - b.trade.sort_order)) {
      groups.push(groupOf(`trade:${trade.id}`, "quote", trade.name, lines, width));
    }
    if (orphans.length > 0) {
      groups.push(groupOf("orphan", "orphan", customer ? CUSTOMER_STATEMENT_ORPHAN_LABEL : STATEMENT_ORPHAN_LABEL, orphans, width));
    }
  } else {
    const order = new Map(input.spaces.map((s, i) => [s.code, i]));
    // 목록 밖 코드는 라벨이 같으므로 한 묶음으로 모은다(공간 목록 순 → 목록 밖 → 미지정).
    const bucketOf = (code: string | null): { key: string; rank: number; label: string } =>
      code === null
        ? { key: "space:", rank: Number.MAX_SAFE_INTEGER, label: UNASSIGNED_SPACE_LABEL }
        : order.has(code)
          ? { key: `space:${code}`, rank: order.get(code)!, label: spaceLabel.get(code)! }
          : { key: "space-unlisted", rank: input.spaces.length, label: unlistedLabel };
    const bySpace = new Map<string, { label: string; lines: StatementLine[] }>();
    const sorted = [...quoteLines].sort((a, b) => bucketOf(a.leaf.space_code).rank - bucketOf(b.leaf.space_code).rank || a.leaf.order - b.leaf.order);
    for (const { leaf, line } of sorted) {
      const bucket = bucketOf(leaf.space_code);
      const group = bySpace.get(bucket.key) ?? { label: bucket.label, lines: [] };
      group.lines.push(line);
      bySpace.set(bucket.key, group);
    }
    for (const [key, { label, lines }] of bySpace) groups.push(groupOf(key, "quote", label, lines, width));
  }
  if (free.length > 0) groups.push(groupOf("free", "free", CHANGE_ORDER_FREE_LINE_LABEL, free.map(finish), width));
  if (legacy.length > 0) groups.push(groupOf("legacy", "legacy", STATEMENT_LEGACY_DEDUCT_LABEL, legacy.map(finish), width));

  const lineOriginal = sum(
    groups.map((g) => g.original),
    "원 금액"
  );
  const lineDeltas = columns.map((_, col) =>
    sum(
      groups.map((g) => g.deltas[col]),
      "증감액"
    )
  );
  const lineCurrent = sum(
    groups.map((g) => g.current),
    "현재 금액"
  );
  const mgmt_fee = input.quote?.quote.mgmt_fee ?? 0;
  // 별도면 견적 공급가(= gross − 절사액)와 계약 공급가로 맞춘다 — 총액으로 대조하면 부가세가 절사·조정으로 새어 든다.
  const quoteNet = input.quote ? (excl ? storedSplitOf(input.quote.quote).supply_amount : input.quote.quote.total) : 0;
  const rounding = input.quote ? checked(quoteNet - input.quote.quote.gross, "절사") : 0;
  const original = input.contract.total_amount;
  const base = excl ? input.contract.supply_amount : original;
  const contract_adjust = checked(base - lineOriginal - mgmt_fee - rounding, "계약금액 조정");
  // 현재 계약금액은 계약 상세·변경공사와 같은 currentTotalOf 로 낸다 — 위 줄들은 그 수를 나눠 보일 뿐이다.
  const current = currentTotalOf(
    input.contract,
    accepted.map((co) => co.change_order)
  );

  const reason: ContractAdjustReason =
    contract_adjust === 0 ? null : !input.quote ? "NO_QUOTE" : input.quote.quote.kind === "ROUGH" ? "ROUGH" : "EDITED";

  return {
    grouping: input.grouping,
    columns,
    groups,
    totals: {
      vat_mode: excl ? "EXCL" : "INCL",
      lines: { original: lineOriginal, deltas: lineDeltas, current: lineCurrent },
      mgmt_fee,
      rounding,
      contract_adjust,
      change_adjust: columns.map((c) => c.adjust),
      total: { original, deltas: columns.map((c) => c.delta_total), current: current.current },
      original_supply: input.contract.supply_amount,
      original_vat: input.contract.vat_amount,
      current_supply: current.current_supply,
      current_vat: current.current_vat,
    },
    contract_adjust_reason: reason,
  };
};

export const CONTRACT_ADJUST_REASON_LABEL: Record<Exclude<ContractAdjustReason, null>, string> = {
  NO_QUOTE: "원 견적을 읽지 못해 줄 내역 없이 계약금액만 둡니다",
  ROUGH: "가견적 계약이라 줄 내역이 없습니다",
  EDITED: "견적 합계와 계약서 금액의 차이입니다",
};

export interface StatementSummaryRow {
  key: "lines" | "mgmt" | "rounding" | "contract_adjust" | "change_adjust" | "supply" | "vat" | "total";
  label: string;
  note: string | null;
  original: number | null;
  deltas: (number | null)[];
  current: number;
}

// 누가 보는 표인가 — 고객은 내부 사정(견적을 못 읽음·가견적·계약서 금액 수정)을 가르지 않는 문구로 본다.
export type StatementAudience = "staff" | "customer";

export const CUSTOMER_CONTRACT_ADJUST_LABEL = "계약 시 조정";
export const CUSTOMER_CONTRACT_ADJUST_NOTE = "계약서 금액에 맞춘 차이입니다";
// 줄 내역이 없는 계약(가견적 · 원 견적 없음) — 계약 전체가 「조정」 으로 읽히지 않게 한다.
export const CUSTOMER_LUMP_SUM_LABEL = "일괄 계약 금액";
export const CUSTOMER_LUMP_SUM_NOTE = "세부 내역 없이 계약한 금액입니다";
export const CUSTOMER_CHANGE_ADJUST_LABEL = "변경계약 금액 맞춤";
export const CUSTOMER_CHANGE_ADJUST_NOTE = "변경계약서 금액에 맞춘 차이입니다";
export const CUSTOMER_STATEMENT_NOTE = "승인된 변경공사까지 반영한 금액입니다";

// 표 아래 합계 줄 — 줄 합 → 관리비 → 절사 → (조정) → (별도면 공급가액 → 부가세) → 계약금액 순이고 화면·인쇄·고객 화면이 같이 쓴다(금액은 같고 문구만 audience 로 가른다).
export const statementSummaryRows = (s: CurrentStatement, audience: StatementAudience = "staff"): StatementSummaryRow[] => {
  const t = s.totals;
  const customer = audience === "customer";
  const blank = s.columns.map(() => null);
  const excl = t.vat_mode === "EXCL";
  const linesNote = customer ? "관리비·단수 정리 전" : "관리비·절사 전";
  const rows: StatementSummaryRow[] = [
    {
      key: "lines",
      label: "항목 합계",
      note: excl ? `${linesNote} · ${vatBasisLabel("EXCL")}` : linesNote,
      original: t.lines.original,
      deltas: t.lines.deltas,
      current: t.lines.current,
    },
  ];
  if (t.mgmt_fee !== 0) rows.push({ key: "mgmt", label: "관리비", note: "원 견적 그대로", original: t.mgmt_fee, deltas: blank, current: t.mgmt_fee });
  if (t.rounding !== 0) rows.push({ key: "rounding", label: "단수 정리", note: null, original: t.rounding, deltas: blank, current: t.rounding });
  if (t.contract_adjust !== 0) {
    const staffNote = s.contract_adjust_reason ? CONTRACT_ADJUST_REASON_LABEL[s.contract_adjust_reason] : null;
    const lumpSum = s.contract_adjust_reason === "ROUGH" || s.contract_adjust_reason === "NO_QUOTE";
    rows.push({
      key: "contract_adjust",
      label: !customer ? "계약금액 조정" : lumpSum ? CUSTOMER_LUMP_SUM_LABEL : CUSTOMER_CONTRACT_ADJUST_LABEL,
      note: !customer ? staffNote : lumpSum ? CUSTOMER_LUMP_SUM_NOTE : CUSTOMER_CONTRACT_ADJUST_NOTE,
      original: t.contract_adjust,
      deltas: blank,
      current: t.contract_adjust,
    });
  }
  if (t.change_adjust.some((v) => v !== 0)) {
    rows.push({
      key: "change_adjust",
      label: customer ? CUSTOMER_CHANGE_ADJUST_LABEL : "변경 조정",
      note: customer ? CUSTOMER_CHANGE_ADJUST_NOTE : excl ? "변경공사 공급가 증감과 항목 합의 차이" : "변경공사 증감액과 항목 합의 차이",
      original: null,
      deltas: t.change_adjust.map((v) => (v === 0 ? null : v)),
      current: sum(t.change_adjust, "변경 조정"),
    });
  }
  if (excl) {
    rows.push({ key: "supply", label: SUPPLY_LABEL, note: null, original: t.original_supply, deltas: s.columns.map((c) => c.delta_supply), current: t.current_supply });
    rows.push({ key: "vat", label: VAT_LABEL, note: null, original: t.original_vat, deltas: s.columns.map((c) => c.delta_vat), current: t.current_vat });
  }
  rows.push({ key: "total", label: "계약금액", note: "부가세 포함", original: t.total.original, deltas: t.total.deltas, current: t.total.current });
  return rows;
};

// 인쇄 A4 폭 — 차수 열 3개까지는 그대로 · 5개까지는 글자를 줄이고 · 6개부터는 「변경 합계」 한 열 + 차수별 부록.
export type StatementPrintMode = "each" | "dense" | "merged";
export const STATEMENT_PRINT_EACH_MAX = 3;
export const STATEMENT_PRINT_DENSE_MAX = 5;
export const statementPrintModeOf = (columnCount: number): StatementPrintMode =>
  columnCount <= STATEMENT_PRINT_EACH_MAX ? "each" : columnCount <= STATEMENT_PRINT_DENSE_MAX ? "dense" : "merged";

// 「변경 합계」 칸 — 차수별 증감을 더한다(건드린 차수가 없으면 null).
export const mergedDeltaOf = (deltas: readonly (StatementDelta | null)[]): StatementDelta | null => {
  const touched = deltas.filter((d): d is StatementDelta => d !== null);
  if (touched.length === 0) return null;
  return {
    qty: touched.reduce((a, d) => a + milli(d.qty), 0) / 1000,
    amount: sum(
      touched.map((d) => d.amount),
      "증감액"
    ),
  };
};

// 소계·합계 줄의 「변경 합계」 — 모두 비었으면 null.
export const mergedAmountOf = (values: readonly (number | null)[]): number | null => {
  const touched = values.filter((v): v is number => v !== null);
  return touched.length === 0 ? null : sum(touched, "증감액");
};

export interface StatementAppendixEntry {
  column: StatementColumn;
  lines: { key: string; label: string; unit: string; qty: number; amount: number }[];
}

// 합친 인쇄본의 차수별 부록 — 차수마다 그 차수가 건드린 줄만 표 순서대로.
export const statementAppendixOf = (statement: Pick<CurrentStatement, "columns" | "groups">): StatementAppendixEntry[] =>
  statement.columns.map((column, col) => ({
    column,
    lines: statement.groups.flatMap((g) =>
      g.lines.flatMap((l) => {
        const d = l.deltas[col];
        return d ? [{ key: l.key, label: l.label, unit: l.unit, qty: d.qty, amount: d.amount }] : [];
      })
    ),
  }));

export const statementPath = (contractId: string, grouping: StatementGrouping = "trade"): string =>
  `/app/contracts/${contractId}/statement${grouping === "space" ? "?by=space" : ""}`;

export const statementPrintPath = (contractId: string, grouping: StatementGrouping = "trade"): string =>
  `/app/contracts/${contractId}/statement/print${grouping === "space" ? "?by=space" : ""}`;

export const customerStatementPath = (token: string, grouping: StatementGrouping = "trade"): string =>
  `${contractSharePath(token)}/statement${grouping === "space" ? "?by=space" : ""}`;
